import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryDefinition,
} from '@reduxjs/toolkit/dist/query';
import { LazyQueryTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { InViewHookResponse, useInView } from 'react-intersection-observer';

import { PaginableList, PaginableRequest } from 'src/services/redux/api';
import { deepEqual } from 'src/utils/common';

export type FetchTriggerRef = InViewHookResponse['ref'];

interface UsePaginableListProps<T, Request extends PaginableRequest> {
  disabled?: boolean;
  extraReqArgs?: Omit<Request, 'limit' | 'page'>;
  fetchList: LazyQueryTrigger<
    QueryDefinition<
      Request,
      BaseQueryFn<
        string | FetchArgs,
        unknown,
        FetchBaseQueryError,
        {},
        FetchBaseQueryMeta
      >,
      string,
      PaginableList<T>,
      string
    >
  >;
  invalidateDeps?: Record<number | string, any>;
  invalidateItem?: (item: T) => boolean;
  mapItem?: (item: T) => T;
  isFetching: boolean;
  limit?: number;
}

export const usePaginableList = <T, Request extends PaginableRequest>({
  disabled,
  extraReqArgs,
  fetchList,
  invalidateDeps,
  invalidateItem,
  mapItem,
  isFetching,
  limit = 10,
}: UsePaginableListProps<T, Request>) => {
  const { inView: isFetchTriggerInView, ref: fetchTriggerRef } = useInView();

  const [list, setList] = useState<T[]>([]);
  const [total, setTotal] = useState(0);

  const extraReqArgsRef = useRef(extraReqArgs);
  const invalidateDepsRef = useRef(invalidateDeps);
  const limitRef = useRef(limit);

  const getInvalidatedList = useCallback(
    (ls: T[]) => {
      const filtered = typeof invalidateItem === 'function'
        ? ls.filter((i) => !invalidateItem(i))
        : ls;
      return typeof mapItem === 'function' ? filtered.map(mapItem) : filtered;
    },
    [invalidateItem, mapItem, list],
  );

  const getInvalidatedListRef = useRef(getInvalidatedList);
  getInvalidatedListRef.current = getInvalidatedList;

  const onFetchList = useCallback(
    async (currentList: T[]) => {
      if (disabled || isFetching) {
        return;
      }

      const nextPage = Math.floor(currentList.length / limit) + 1;

      try {
        const res = await fetchList({
          limit,
          page: nextPage,
          ...extraReqArgs,
        } as Request).unwrap();

        const updatedList = getInvalidatedListRef.current([...currentList, ...res.items]);
        setList(updatedList);
        setTotal(res.meta.total);
      } catch (err) {
        console.log('fetch paginable list failure', err);
      }
    },
    [disabled, extraReqArgs, isFetching, limit],
  );

  const onRefetchList = useCallback(async () => {
    try {
      const res = await fetchList({
        limit,
        page: 1,
        ...extraReqArgs,
      } as Request).unwrap();
      setList(getInvalidatedListRef.current(res.items));
      setTotal(res.meta.total);
    } catch (err) {
      console.log('fetch paginable list failure', err);
    }
  }, [fetchList, limit, extraReqArgs]);

  useEffect(() => {
    onFetchList([]);
  }, []);

  useEffect(() => {
    if (isFetchTriggerInView && !isFetching && list.length < total) {
      onFetchList(list);
    }
  }, [isFetchTriggerInView, isFetching, list.length, total]);

  useEffect(() => {
    if (
      !deepEqual(extraReqArgs || {}, extraReqArgsRef.current || {}) ||
      limit !== limitRef.current
    ) {
      extraReqArgsRef.current = extraReqArgs;
      limitRef.current = limit;

      onFetchList([]);
      setTotal(0);
    }
  }, [extraReqArgs, limit]);

  useEffect(() => {
    if (!deepEqual(invalidateDeps || {}, invalidateDepsRef.current || {})) {
      invalidateDepsRef.current = invalidateDeps;
      const updatedList = getInvalidatedList(list);
      setList(updatedList);
      setTotal(total - (list.length - updatedList.length));
    }
  }, [getInvalidatedList, invalidateDeps, list]);

  return { fetchTriggerRef, list, onRefetchList, total };
};
